import { Box, Button, Card, Flex, Grid, IconButton, Select, Separator, Text } from '@radix-ui/themes';
import { format } from 'date-fns';
import getDay from 'date-fns/getDay';
import getDaysInMonth from 'date-fns/getDaysInMonth';
import isFuture from 'date-fns/isFuture';
import subMonths from 'date-fns/subMonths';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import Icon from '~/components/Icon';
import onClickOutside from '~/hooks/use-on-click-outside';
import TimeInput from '../Input/Elements/Time';

const DAY_BOX_SIZE = '32px';

const Wrapper = styled.div`
  position: relative;
  z-index: ${props => props.theme.zIndex.input};
`;

const CalenderBase = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: var(--radius-1);
`;

const CalenderBaseWithEffects = styled(CalenderBase)`
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};

  ${props =>
    props.selected &&
    css`
      background-color: var(--orange-a4);
    `}
  ${props =>
    !props.disabled &&
    css`
      &:hover {
        background-color: var(--neutral-a3);
      }
    `}
`;

const CalenderMonthItem = styled(CalenderBaseWithEffects)`
  width: 84px;
  height: 36px;
`;

function CalenderMonthItems({ displayedYear, minYear, minMonth, selectedYear, selectedMonthZeroIndex, setValue }) {
  return (
    <Grid
      columns='3'
      wrap='wrap'
      justify='between'
    >
      {MONTHS.map((month, monthIdx) => {
        const disabled =
          isFuture(new Date(displayedYear, monthIdx)) || (minYear === displayedYear && minMonth > monthIdx + 1);
        const selected = selectedYear === displayedYear && monthIdx === selectedMonthZeroIndex;

        return (
          <CalenderMonthItem
            key={month}
            selected={selected}
            onClick={() => !disabled && setValue(monthIdx + 1)}
            disabled={disabled}
          >
            <CalenderItemText
              disabled={disabled}
              weight={selected ? 'bold' : 'normal'}
            >
              {month}
            </CalenderItemText>
          </CalenderMonthItem>
        );
      })}
    </Grid>
  );
}

CalenderMonthItems.propTypes = {
  displayedYear: PropTypes.number.isRequired,
  minYear: PropTypes.number.isRequired,
  minMonth: PropTypes.number.isRequired,
  selectedYear: PropTypes.number.isRequired,
  selectedMonthZeroIndex: PropTypes.number.isRequired,
  setValue: PropTypes.func.isRequired,
};

const CalenderWeekDayItem = styled(CalenderBase)`
  width: ${DAY_BOX_SIZE};
  height: 36px;
`;

const CalenderDayItem = styled(CalenderBaseWithEffects)`
  width: ${DAY_BOX_SIZE};
  height: ${DAY_BOX_SIZE};
`;

function CalenderDayItems({
  SunSatToMonSunConverter,
  displayedYear,
  displayedMonth,
  displayedDaysToRender,
  indexOfFirstDayOfTheMonth,
  displayedMonthZeroIndex,
  minYear,
  minMonth,
  minDay,
  setDayValue,
  selectedYear,
  selectedMonth,
  selectedDay,
}) {
  return (
    <Grid columns='7'>
      {Array(SunSatToMonSunConverter(indexOfFirstDayOfTheMonth)).fill(
        <Box
          width={DAY_BOX_SIZE}
          height={DAY_BOX_SIZE}
        />,
      )}
      {displayedDaysToRender.map(monthlyDay => {
        const isInTheFuture = isFuture(new Date(displayedYear, displayedMonthZeroIndex, monthlyDay));
        const disabled =
          isInTheFuture || (minYear === displayedYear && minMonth === displayedMonth && minDay > monthlyDay);
        const selected =
          selectedYear === displayedYear && selectedMonth === displayedMonth && selectedDay === monthlyDay;

        return (
          <CalenderDayItem
            key={monthlyDay}
            disabled={disabled}
            onClick={() => !disabled && setDayValue(monthlyDay)}
            selected={selected}
          >
            <CalenderItemText
              disabled={disabled}
              weight={selected ? 'bold' : 'normal'}
            >
              {monthlyDay}
            </CalenderItemText>
          </CalenderDayItem>
        );
      })}
    </Grid>
  );
}

CalenderDayItems.propTypes = {
  displayedYear: PropTypes.number.isRequired,
  displayedMonth: PropTypes.number.isRequired,
  displayedDaysToRender: PropTypes.arrayOf(PropTypes.number).isRequired,
  indexOfFirstDayOfTheMonth: PropTypes.number.isRequired,
  displayedMonthZeroIndex: PropTypes.number.isRequired,
  SunSatToMonSunConverter: PropTypes.func.isRequired,
  minYear: PropTypes.number.isRequired,
  minMonth: PropTypes.number.isRequired,
  minDay: PropTypes.number.isRequired,
  setDayValue: PropTypes.func.isRequired,
  selectedYear: PropTypes.number.isRequired,
  selectedMonth: PropTypes.number.isRequired,
  selectedDay: PropTypes.number.isRequired,
};

const CalenderItemText = styled(Text)`
  ${props =>
    props.disabled &&
    css`
      color: var(--neutral-8);
    `}
`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DAYS = ['Ma', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function getTimestampBreakdown(timestamp) {
  const date = new Date(timestamp);
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    time: date.toTimeString().slice(0, 5),
  };
}

function getLabelForTimestamp(timestamp, withDays, withTime) {
  const { year, month, day, time = '00:00' } = getTimestampBreakdown(timestamp);
  const base = `${year} - ${MONTHS[month - 1]}`;
  if (withTime) return `${base} - ${day}, ${time}`;
  if (withDays) return `${base} - ${day}`;
  return base;
}

function MonthPicker({
  withDays,
  withTime,
  value,
  onChange,
  minYear = 2018,
  minMonth = false,
  minDay = false,
  limitMonths,
}) {
  if (withTime && !withDays) {
    throw new Error('MonthPicker: withTime can only be used together with withDays');
  }

  const today = new Date();
  const { year, month, day, time } = getTimestampBreakdown(value);
  const confirmOnSelect = !withTime;

  // Currently selected values - The value that will be set on onChange
  const [selectedYear, setSelectedYear] = useState(year);
  const [selectedMonth, setSelectedMonth] = useState(month);
  const [selectedDay, setSelectedDay] = useState(day);
  const [selectedTime, setSelectedTime] = useState(time);
  // Currently displayed values - The value that is shown in the picker
  const [displayedYear, setDisplayedYear] = useState(selectedYear);
  const [displayedMonth, setDisplayedMonth] = useState(selectedMonth);

  const [monthIsOpen, setMonthIsOpen] = useState(false);
  const [daysIsOpen, setDaysIsOpen] = useState(false);
  const daySelectRef = useRef(null);
  const monthSelectRef = useRef(null);

  const setSelectedAndDisplayedValues = useCallback(() => {
    const { year, month, day, time } = getTimestampBreakdown(value);

    setSelectedYear(year);
    setSelectedMonth(month);
    setSelectedDay(day);
    setSelectedTime(time);
    setDisplayedYear(year);
    setDisplayedMonth(month);
  }, [value]);

  useEffect(() => {
    setSelectedAndDisplayedValues();
  }, [setSelectedAndDisplayedValues]);

  onClickOutside(monthSelectRef, () => {
    if (monthIsOpen) setMonthIsOpen(false);
  });

  onClickOutside(daySelectRef, () => {
    if (daysIsOpen && !monthIsOpen) {
      setSelectedAndDisplayedValues();
      setDaysIsOpen(false);
    }
  });

  const selectedMonthZeroIndex = selectedMonth - 1;
  const displayedMonthZeroIndex = displayedMonth - 1;
  const displayedDaysToRender = [...Array(getDaysInMonth(new Date(selectedYear, displayedMonthZeroIndex))).keys()].map(
    d => d + 1,
  );
  const firstDayOfTheMonth = new Date(displayedYear, displayedMonthZeroIndex, 1);
  const indexOfFirstDayOfTheMonth = getDay(firstDayOfTheMonth);

  function SunSatToMonSunConverter(dayOfWeekNumber) {
    // getting empty days to put in the day picker before the 1st day of the month
    // passing as a prop is an index of a weekday 0 - 6 (sunday being 0)
    // and in the day picker the days start with Monday (sunday being 6)
    if (dayOfWeekNumber === 1) {
      // here prop is Monday so there should be 0 empty cells in the day picker
      return 0;
    }
    if (dayOfWeekNumber === 0) {
      // here prop is Sunday so there should be 6 empty cells in the day picker
      return 6;
    }
    // for the rest of the days we remove 1 and we get the number of the empty cells in the day picker
    return dayOfWeekNumber - 1;
  }

  if (limitMonths) {
    const limit = subMonths(today, limitMonths);
    minYear = limit.getFullYear();
    minMonth = limit.getMonth() + 1;
  }

  function setMonthValue(newMonth) {
    setMonthIsOpen(false);
    if (withDays) {
      setDaysIsOpen(true);
      setDisplayedMonth(newMonth);
    } else {
      onChange(`${displayedYear}-${newMonth.toString().padStart(2, '0')}`);
    }
  }

  function setDayValue(day) {
    if (confirmOnSelect) {
      onChange(`${displayedYear}-${displayedMonth.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`);
      setMonthIsOpen(false);
      setDaysIsOpen(false);
      return;
    }

    setSelectedDay(day);
    setSelectedMonth(displayedMonth);
    setSelectedYear(displayedYear);
    if (withTime) setSelectedTime('');
  }

  function nextMonth() {
    const next = displayedMonth + 1;
    if (next > 12) {
      setDisplayedYear(prev => prev + 1);
      setDisplayedMonth(1);
    } else {
      setDisplayedMonth(next);
    }
  }

  function prevMonth() {
    const prev = displayedMonth - 1;
    if (prev < 1) {
      setDisplayedYear(prev => prev - 1);
      setDisplayedMonth(12);
    } else {
      setDisplayedMonth(prev);
    }
  }

  const closePopup = useCallback(() => {
    setSelectedAndDisplayedValues();
    setMonthIsOpen(false);
    setDaysIsOpen(false);
  }, [setSelectedAndDisplayedValues]);

  const onKeyDown = useCallback(
    e => {
      if (e.key === 'Escape') {
        if (monthIsOpen) return setMonthIsOpen(false);
        closePopup();
      }
    },
    [closePopup, monthIsOpen],
  );

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onKeyDown]);

  const isCurrentMonth = displayedYear === today.getFullYear() && displayedMonth === today.getMonth() + 1;
  const isLastMonth = displayedYear === minYear && displayedMonth === minMonth;

  function onConfirmClick(time) {
    const timeValue = time || '00:00';
    const newValue = format(
      new Date(`${selectedYear}-${selectedMonth}-${selectedDay} ${timeValue}`),
      'yyyy-MM-dd HH:mm',
    );
    onChange(newValue);
    closePopup();
  }

  function MonthPopover() {
    return (
      <Box
        mt='1'
        position='absolute'
        asChild
        width='264px'
        ref={monthSelectRef}
      >
        <Card>
          <Flex
            direction='column'
            gap='3'
          >
            <Flex
              justify='between'
              align='center'
            >
              <IconButton
                size='1'
                disabled={minYear === displayedYear}
                onClick={() => setDisplayedYear(displayedYear - 1)}
                color='gray'
                variant='outline'
              >
                <Icon
                  name='chevron-left'
                  width={16}
                  height={16}
                  inline
                  color={minYear === displayedYear ? 'var(--neutral-a8)' : 'var(--neutral-12)'}
                />
              </IconButton>
              <Text
                size='3'
                weight='bold'
              >
                {displayedYear}
              </Text>
              <IconButton
                size='1'
                disabled={displayedYear === today.getFullYear()}
                onClick={() => setDisplayedYear(displayedYear + 1)}
                color='gray'
                variant='outline'
              >
                <Icon
                  name='chevron-right'
                  width={16}
                  height={16}
                  inline
                  color={displayedYear === today.getFullYear() ? 'var(--neutral-8)' : 'var(--neutral-12)'}
                />
              </IconButton>
            </Flex>
            <CalenderMonthItems
              displayedYear={displayedYear}
              minYear={minYear}
              minMonth={minMonth}
              selectedYear={selectedYear}
              selectedMonthZeroIndex={selectedMonthZeroIndex}
              setValue={setMonthValue}
            />
          </Flex>
        </Card>
      </Box>
    );
  }

  function DayPopover() {
    return (
      <Box
        mt='1'
        position='absolute'
        asChild
        width='264px'
        ref={daySelectRef}
      >
        <Card>
          <Flex
            direction='column'
            gap='1'
          >
            <Flex justify='between'>
              <IconButton
                size='1'
                color='gray'
                variant='outline'
                onClick={prevMonth}
                disabled={isLastMonth}
              >
                <Icon
                  name='chevron-left'
                  width={16}
                  height={16}
                  inline
                  color={isLastMonth ? 'var(--neutral-8)' : 'var(--neutral-12)'}
                />
              </IconButton>
              <Button
                size='1'
                color='gray'
                onClick={() => setMonthIsOpen(true)}
                variant='outline'
              >
                {displayedYear} - {MONTHS[displayedMonthZeroIndex]}
              </Button>
              <IconButton
                size='1'
                color='gray'
                variant='outline'
                onClick={nextMonth}
                disabled={isCurrentMonth}
              >
                <Icon
                  name='chevron-right'
                  width={16}
                  height={16}
                  inline
                  color={isCurrentMonth ? 'var(--neutral-8)' : 'var(--neutral-12)'}
                />
              </IconButton>
            </Flex>
            <Grid columns='7'>
              {DAYS.map(day => {
                return (
                  <CalenderWeekDayItem key={day}>
                    <CalenderItemText>{day}</CalenderItemText>
                  </CalenderWeekDayItem>
                );
              })}
            </Grid>
            <CalenderDayItems
              displayedYear={displayedYear}
              displayedMonth={displayedMonth}
              displayedDaysToRender={displayedDaysToRender}
              displayedMonthZeroIndex={displayedMonthZeroIndex}
              indexOfFirstDayOfTheMonth={indexOfFirstDayOfTheMonth}
              SunSatToMonSunConverter={SunSatToMonSunConverter}
              minYear={minYear}
              minMonth={minMonth}
              minDay={minDay}
              setDayValue={setDayValue}
              selectedYear={selectedYear}
              selectedMonth={selectedMonth}
              selectedDay={selectedDay}
            />
            {withDays && withTime && (
              <>
                <Text
                  size='2'
                  weight='regular'
                >
                  Start time (UTC+0)
                </Text>
                <TimeInput
                  onBlur={setSelectedTime}
                  onEnter={onConfirmClick}
                  defaultValue={selectedTime}
                />
                <Separator
                  my='2'
                  size='4'
                />
                <Grid
                  columns='2'
                  gap='1'
                >
                  <Box
                    asChild
                    mx='2'
                  >
                    <Button
                      size='1'
                      color='gray'
                      radius='full'
                      variant='ghost'
                      onClick={closePopup}
                    >
                      Cancel
                    </Button>
                  </Box>
                  <Box
                    asChild
                    mx='2'
                  >
                    <Button
                      size='1'
                      color='gray'
                      radius='full'
                      variant='ghost'
                      onClick={() => onConfirmClick(selectedTime)}
                    >
                      Apply
                    </Button>
                  </Box>
                </Grid>
              </>
            )}
          </Flex>
        </Card>
      </Box>
    );
  }

  function toggleDisplay() {
    if (withDays) return setDaysIsOpen(prev => !prev);
    setMonthIsOpen(prev => !prev);
  }

  const selectLabel = getLabelForTimestamp(value, withDays, withTime);

  return (
    <Flex gap='2'>
      <Wrapper
        active={monthIsOpen}
        withDays={withDays}
      >
        <Select.Root value='Hack to make the trigger show its content'>
          <Box
            asChild
            width='220px'
          >
            <Select.Trigger onClick={toggleDisplay}>{selectLabel}</Select.Trigger>
          </Box>
        </Select.Root>
        {daysIsOpen && <DayPopover />}
        {monthIsOpen && <MonthPopover />}
      </Wrapper>
    </Flex>
  );
}

MonthPicker.propTypes = {
  withDays: PropTypes.bool,
  withTime: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  minYear: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  minMonth: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  minDay: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  limitMonths: PropTypes.number,
};

export default React.memo(MonthPicker, (prevProps, nextProps) => {
  return prevProps.value === nextProps.value;
});
